import {
  AutocompleteInputRawForm,
  Button,
  CheckboxForm,
  SelectForm,
  TextInputForm,
} from '@altify/components';
import { useContent } from '@altify/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useProductWithDepositInfo } from 'hooks/products/useProducts';
import { useForm } from 'react-hook-form';
import {
  useGetCryptoAccountQuery,
  useGetVaspsQuery,
} from 'store/slices/crypto-accounts-api';
import * as z from 'zod';
import CryptoWithdrawalWrapper from '../../../components/CryptoWithdrawalWrapper';
import { content } from './route.content';

export const Route = createFileRoute(
  '/_authenticated/funds/withdraw/crypto/$productCode/wallet/edit/details',
)({
  component: WithdrawCryptoAccountDetails,
});

const walletTypes = [
  { label: 'Self Hosted', value: 'selfHosted' },
  { label: 'Account on another platform', value: 'accountOnAnotherPlatform' },
];

const schema = z
  .object({
    address: z.string().min(1),
    network: z.string().min(1),
    institution: z
      .object({ label: z.string().nullable(), value: z.string().nullable() })
      .optional(),
    walletType: z.string().min(1).max(255),
    confirmation: z
      .boolean()
      .refine(v => v == true, { message: 'You need to confirm' })
      .default(false),
  })
  .refine(
    data => {
      if (data.walletType === 'accountOnAnotherPlatform') {
        return data.institution?.label?.trim().length ?? 0 > 0;
      }
      return true;
    },
    {
      message: 'Platform is required when Wallet Type is not Self Hosted',
      path: ['institution'],
    },
  );
type FormSchema = z.infer<typeof schema>;

function WithdrawCryptoAccountDetails() {
  const navigate = useNavigate();
  const localizedContent = useContent(content);
  const { productCode } = Route.useParams();
  const { product } = useProductWithDepositInfo(productCode);
  const { data: vasps, isLoading: vaspsLoading } = useGetVaspsQuery();
  const institutions = vasps?.map(v => ({ label: v.name!, value: v.id! }));

  const { data: accountData, isLoading } = useGetCryptoAccountQuery(
    productCode!,
    { refetchOnMountOrArgChange: true },
  );

  const initialValues = accountData;
  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    values: {
      address: initialValues?.address ?? '',
      walletType:
        initialValues?.isHosted === undefined
          ? ''
          : initialValues?.isHosted
            ? 'accountOnAnotherPlatform'
            : 'selfHosted',
      institution: {
        value: initialValues?.institutionId ?? null,
        label:
          (initialValues?.institutionId
            ? vasps?.find(v => v.id === initialValues?.institutionId)?.name
            : initialValues?.name) ?? '',
      },
      network: product?.network ?? '',
      confirmation: false,
    },
  });

  const onSubmit = handleSubmit(async data => {
    navigate({
      to: '/funds/withdraw/crypto/$productCode/wallet/edit/confirm',
      params: { productCode: productCode },
      search: {
        accountId: accountData?.accountId ?? undefined,
        address: data.address,
        name: data.institution?.label ?? undefined,
        isHosted: data.walletType === 'accountOnAnotherPlatform',
        institutionId: data.institution?.value ?? undefined,
      },
    });
  });

  const walletType = watch('walletType');
  const bodyContent = (
    <>
      <SelectForm
        control={control}
        name="walletType"
        label={localizedContent.walletType}
        items={walletTypes}
      />

      {walletType === 'accountOnAnotherPlatform' && (
        <AutocompleteInputRawForm
          control={control}
          name="institution"
          label={localizedContent.platformName}
          items={institutions!}
          acceptOther
        />
      )}
      <TextInputForm
        control={control}
        name="address"
        label={localizedContent.title(product?.name!)}
      />
      <TextInputForm
        readonly={true}
        control={control}
        name="network"
        label={localizedContent.network}
      />
      {walletType && (
        <CheckboxForm
          control={control}
          name="confirmation"
          label={
            walletType === 'selfHosted'
              ? localizedContent.selfHostedWallet
              : localizedContent.hostedWallet
          }
        />
      )}
    </>
  );

  const buttons = (
    <div className="pb-20 w-full">
      <Button
        onPress={onSubmit}
        className="mt-4 w-full"
        text={localizedContent.next}
        disabled={!isValid}
      />
    </div>
  );
  return (
    <CryptoWithdrawalWrapper
      isLoading={isLoading || vaspsLoading}
      title={localizedContent.title(product?.name!)}
      description={localizedContent.description}
      bodyContent={bodyContent}
      buttons={buttons}
    />
  );
}
